/**
 * Hur gästsessionen skapades. 'bankid' = BankID-onboarding (dagens flöde),
 * 'none' = öppen butik utan inloggning (docs/OPEN_SHOP.md). Tokens utan
 * claim är äldre BankID-sessioner.
 */
export type GuestVerificationMethod = 'bankid' | 'none';

export function createGuestToken(data: {
  identifierHash: string;
  organizationId: string;
  deviceId: string;
  sessionId: string;
  verificationMethod?: GuestVerificationMethod;
}): string {
  const payload = {
    identifier_hash: data.identifierHash,
    organization_id: data.organizationId,
    device_id: data.deviceId,
    session_id: data.sessionId,
    verification_method: data.verificationMethod ?? 'bankid',
    type: 'guest',
    exp: Date.now() + (24 * 60 * 60 * 1000) // 24 timmar
  };
  
  // Base64-koda payload (enkel version utan signatur)
  const header = btoa(JSON.stringify({ alg: 'none', typ: 'JWT' }));
  const encodedPayload = btoa(JSON.stringify(payload));
  
  return `${header}.${encodedPayload}.`;
}

export function parseGuestToken(token: string): any | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    
    const payload = JSON.parse(atob(parts[1]));
    return payload;
  } catch {
    return null;
  }
}

export function validateGuestToken(token: string): boolean {
  const payload = parseGuestToken(token);
  if (!payload) return false;

  // Check required fields
  if (!payload.type || payload.type !== 'guest') return false;
  if (!payload.organization_id || !payload.device_id) return false;

  // Check expiration
  if (payload.exp && payload.exp < Date.now()) return false;

  return true;
}

/**
 * Verifieringsmetod för den lagrade gästsessionen, eller null utan giltig
 * gästtoken. Används för att leda overifierade gäster (öppen butik) till
 * BankID-landningen i stället för kontokonvertering.
 */
export function getGuestVerificationMethod(): GuestVerificationMethod | null {
  const token = getGuestToken();
  if (!token || !validateGuestToken(token)) return null;
  return parseGuestToken(token)?.verification_method === 'none' ? 'none' : 'bankid';
}

// Guest token in sessionStorage (primary) + localStorage (iOS Safari backup).
// iOS Safari clears sessionStorage on app-switch (Swish/BankID).
// Token has 24h expiry via exp-claim, validated in validateGuestToken().
export function getGuestToken(): string | null {
  try {
    return sessionStorage.getItem('pwa-guest-token')
      || localStorage.getItem('pwa-guest-token')
      || null;
  } catch {
    return null;
  }
}

export function setGuestToken(token: string) {
  try {
    sessionStorage.setItem('pwa-guest-token', token);
    // iOS Safari backup: sessionStorage is cleared on app-switch (Swish/BankID)
    localStorage.setItem('pwa-guest-token', token);
  } catch {
    // ignore storage errors (e.g., Safari private mode)
  }
}

export function clearGuestToken() {
  try {
    sessionStorage.removeItem('pwa-guest-token');
    // Also clear from localStorage if it exists from old sessions
    localStorage.removeItem('pwa-guest-token');
    localStorage.removeItem('guest-organization-id');
  } catch {
    // ignore
  }
}
